import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import { BarCompt, Toggle } from "../../components";
import { getMonth } from "../../utils";
import { iWindow } from "../../config";
import DateSelect from "./DateSelect";
import {
	InsightsContainer,
	DividerSmall,
	Stat,
	GraphContainer,
	StatColumn,
	ChartContainer,
	StatsRow,
	StatItem,
	HeaderOption,
	HeaderRow,
	Loader,
	SectionHeader,
	SampleNotice
} from "./styles";
import mixpanel from 'mixpanel-browser';

interface iProps {
	appointments: Array<any>;
	windowSize?: iWindow | null;
	loading: boolean;
	isSample: boolean;
}
interface iData {
	MONTH?: string;
	data_year?: number;
	data_month?: number;
}
interface iStaff {
	name: string;
	id: string;
	totalAppointments: number;
	appointmentDates: any;
	sales: any;
}
interface iChartType {
	value: "MONEY" | "NUMBER" | string;
}

const CHARTVALUE = {
	NUMBER: "NUMBER",
	MONEY: "MONEY"
};

const sampleChartData = JSON.parse(`[{"MONTH":"May","Rebecca Broadhurst":0,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":0,"Alison Kent":0,"Nick Manfield":0,"Kiera Anderson":0,"(No Name)":0,"data_month":5,"data_year":2020},{"MONTH":"Jun","Rebecca Broadhurst":5092.79,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":0,"Alison Kent":0,"Nick Manfield":0,"Kiera Anderson":0,"(No Name)":0,"data_month":6,"data_year":2020},{"MONTH":"Jul","Rebecca Broadhurst":1843.9,"L'Oreal Academy (Melbourne)":1776,"Susan Bolton":2346,"L'Oreal Academy (Adelaide)":198,"Jo Migliaccio":2013,"Alison Kent":2582.5,"Nick Manfield":0,"Kiera Anderson":0,"(No Name)":0,"data_month":7,"data_year":2020},{"MONTH":"Aug","Rebecca Broadhurst":0,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":0,"Alison Kent":0,"Nick Manfield":1546,"Kiera Anderson":0,"(No Name)":0,"data_month":8,"data_year":2020},{"MONTH":"Sept","Rebecca Broadhurst":229,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":326,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":66,"Alison Kent":0,"Nick Manfield":97,"Kiera Anderson":0,"(No Name)":0,"data_month":9,"data_year":2020},{"MONTH":"Oct","Rebecca Broadhurst":264,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":462,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":190,"Alison Kent":0,"Nick Manfield":163,"Kiera Anderson":0,"(No Name)":0,"data_month":10,"data_year":2020},{"MONTH":"Nov","Rebecca Broadhurst":66,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":66,"Alison Kent":554,"Nick Manfield":0,"Kiera Anderson":0,"(No Name)":0,"data_month":11,"data_year":2020},{"MONTH":"Dec","Rebecca Broadhurst":0,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":97,"Alison Kent":432.74,"Nick Manfield":66,"Kiera Anderson":0,"(No Name)":0,"data_month":12,"data_year":2020},{"MONTH":"Jan","Rebecca Broadhurst":0,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":0,"Alison Kent":0,"Nick Manfield":0,"Kiera Anderson":0,"(No Name)":0,"data_month":1,"data_year":2021},{"MONTH":"Feb","Rebecca Broadhurst":0,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":64,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":293,"Alison Kent":0,"Nick Manfield":0,"Kiera Anderson":350,"(No Name)":0,"data_month":2,"data_year":2021},{"MONTH":"Mar","Rebecca Broadhurst":66,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":0,"Alison Kent":132,"Nick Manfield":447,"Kiera Anderson":0,"(No Name)":0,"data_month":3,"data_year":2021},{"MONTH":"Apr","Rebecca Broadhurst":0,"L'Oreal Academy (Melbourne)":0,"Susan Bolton":0,"L'Oreal Academy (Adelaide)":0,"Jo Migliaccio":0,"Alison Kent":0,"Nick Manfield":0,"Kiera Anderson":175,"(No Name)":0,"data_month":4,"data_year":2021}]`);
const sampleStaffNames = JSON.parse(`["Rebecca Broadhurst","L'Oreal Academy (Melbourne)","Susan Bolton","L'Oreal Academy (Adelaide)","Jo Migliaccio","Alison Kent","Nick Manfield","Kiera Anderson","(No Name)"]`);


export default function Retail (props: iProps) {
	const defaultChartType: iChartType = { value: CHARTVALUE.MONEY };
	const chartDataDefault: iData[] = [];
	const [ productSales, setProductsSales ] = useState(0);
	const [ chartData, setChartData ] = useState(chartDataDefault);
	const [ staffProfiles, setStaffProfiles ] = useState() as any;
	const [ staffNames, setStaffNames ] = useState() as any;
	const [ duration, setDuration ] = useState("all");
	const [ chartType, setChartType ] = useState(defaultChartType);

	useEffect(
		() => {
			countAllSales();
			updateStaffProfiles();
		},
		[ props.appointments ]
	);

	useEffect(
		() => {
			staffProfiles && updateChart();
		},
		[ staffProfiles, duration, chartType.value ]
	);

	const onDateChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setDuration(event.target.value as string);
		mixpanel.track("PMA Dashboard - Retail view changed", {"Changed to view" : event.target.value });
	};

	const onTypeChange = (event: React.ChangeEvent<{}>, checked: boolean) => {
		if (checked) setChartType({ value: CHARTVALUE.MONEY });
		else setChartType({ value: CHARTVALUE.NUMBER });
		mixpanel.track("PMA Dashboard - Retail type changed", {"Changed to type" : checked ?  CHARTVALUE.MONEY : CHARTVALUE.NUMBER });
	};


	const updateStaffProfiles = () => {
		const staffMembers: string[] = [];
		const staff: iStaff[] = [];
		const staffNames: string[] = [];

		props.appointments.map((appointment, i) => {
			/* not yet created so set the staff record */
			if (!staffMembers.includes(appointment.staffId)) {
				staffMembers.push(appointment.staffId);
				/* Only use unique names */
				if (!staffNames.includes(appointment.staffName)) {
					staff.push({
						id: appointment.staffId,
						name: appointment.staffName,
						totalAppointments: 1,
						appointmentDates: [ new Date(appointment.createdAt) ],
						sales: appointment.cartItems
					});
					staffNames.push(appointment.staffName);
				} else {
					/* TODO: UPDATE EXISTING RECORD NAME */
				}
			} else {
				/* Update the existing staff record */
				for (let s = 0; s < staff.length; s++) {
					if (staff[s].id === appointment.staffId) {
						staff[s].totalAppointments += 1;
						staff[s].appointmentDates.push(new Date(appointment.createdAt));
						staff[s].sales.push(...appointment.cartItems);
					}
				}
			}
		});
		setStaffNames(staffNames);
		setStaffProfiles(staff);
	};

	const updateChart = () => {
		let data: iData[] = createChartData();
		setChartData(data);
		console.log("RETAIL DATA", data)
	};

	const createChartData = () => {
		const dataTemp: iData[] = [];
		let staffYear: any[] = [];
		const today = moment();

		if (duration === "all") {
			// FIND EARLIEST DATE 
			let allAppointmentDates = []
			staffProfiles.map(p => { allAppointmentDates = [].concat(allAppointmentDates,p.appointmentDates)});
			let firstDate = moment(new Date(allAppointmentDates.sort(function(a,b){return a - b})[0]));
			let thisYear = moment().year();
			// FOR EACH YEAR
			for (let y = firstDate.year(); y <= thisYear; y++) {
				staffYear = [];
				let totalMonths = 12;
				// IF ITS THIS YEAR, DONT GET FUTURE MONTHS
				if (y === thisYear) {
					totalMonths = moment().month() + 1;
				}
				// FOR EACH MONTH IN THIS YEAR, GET SALES DATA PER STAFF
				for (let m = 0; m < totalMonths; m++) {
					// GET EACH MONTH'S SALES DATA BY STAFF
					let staffMonth = {
						MONTH: (m + 1) + '-' + y,
						data_month: m + 1,
						data_year: y
					};
					staffProfiles.map(profile => {
						staffMonth[profile.name] = roundStat(
								getYearlyMonthlyStat(m, y, profile, chartType.value)
							)
					});
					dataTemp.push(staffMonth);
				}
			}

			/* SORT MEMBER DATA YEAR, CURRENT MONTH FIRST */
			dataTemp.sort(function (b, a) {
				if (a.data_year === b.data_year) {
					return b.data_month && a.data_month
						? b.data_month - a.data_month
						: 0;
				}
				return b.data_year && a.data_year ? b.data_year - a.data_year : 0;
			});
		} else if (duration === "year") {
			const year = moment(new Date()).year();
			for (let m = 0; m < 12; m++) {
				let staffMonth = {};
				staffProfiles.map(profile => {
					staffMonth = {
						...staffMonth,
						[profile.name]: roundStat(
							getMonthlyStat(m, year, profile, chartType.value)
						)
					};
				});
				staffYear.push(staffMonth);
			}

			for (let i = 0; i < 12; i++) {
				const monthResult = {
					MONTH: `${getMonth(i + 1)}`,
					...staffYear[i],
					data_month: i + 1,
					data_year: i > today.month() ? today.year() - 1 : today.year()
				};
				dataTemp.push(monthResult);
				/* SORT MEMBER DATA YEAR, CURRENT MONTH FIRST */
				dataTemp.sort(function (b, a) {
					if (a.data_year === b.data_year) {
						return b.data_month && a.data_month
							? b.data_month - a.data_month
							: 0;
					}
					return b.data_year && a.data_year ? b.data_year - a.data_year : 0;
				});
			}
		} else if (duration === "month") {
			/** CALCS FOR EACH DAY THIS CURRENT MONTH */
			const monthDays = moment(new Date()).daysInMonth();
			const month = moment(new Date()).month();

			for (let day = 0; day < monthDays; day++) {
				let staffMonth = {};
				staffProfiles.map(profile => {
					staffMonth = {
						...staffMonth,
						[profile.name]: roundStat(
							getDailyStat(day, profile, chartType.value, month, null)
						)
					};
				});
				staffYear.push(staffMonth);
			}
			for (let i = 1; i <= monthDays; i++) {
				const monthResult = {
					DAY: `${i.toString()}`,
					...staffYear[i]
				};
				dataTemp.push(monthResult);
			}
		} else if (duration === "week") {
			/** CALCS FOR EACH DAY OF THE WEEK  */
			const monthDays = moment(new Date()).daysInMonth();
			const week = moment(new Date()).week();

			for (let day = 0; day < monthDays; day++) {
				let staffMonth = {};
				staffProfiles.map(profile => {
					staffMonth = {
						...staffMonth,
						[profile.name]: roundStat(
							getDailyStat(day, profile, chartType.value, null, week)
						)
					};
				});
				staffYear.push(staffMonth);
			}
			for (let i = 0; i < 7; i++) {
				const monthResult = {
					DAY: moment().day(i).format("dddd Do"),
					...staffYear[moment().day(i).date()]
				};
				dataTemp.push(monthResult);
			}
		}
		return dataTemp;
	};

	const getMonthlyStat = (month: number, year: number, staffMember: iStaff, type: string) => {
		let sales = 0;
		if (type === CHARTVALUE.NUMBER) {
			props.appointments.map(appointment => {
				const apptDate = moment(new Date(appointment.createdAt));
				if (
					appointment.staffId === staffMember.id &&
					apptDate.month() === month &&
					apptDate.year() === year
				) {
					if (appointment.purchasedItems) {
						sales += appointment.purchasedItems.length;
					} else {
						sales += appointment.cartItems.length;
					}
				}
			});
		} else {
			props.appointments.map(appointment => {
				const apptDate = moment(new Date(appointment.createdAt));
				if (
					appointment.staffId === staffMember.id &&
					apptDate.month() === month &&
					apptDate.year() === year
				) {
				
					if (appointment.purchasedItems) {
						appointment.purchasedItems.map(item => {
							appointment.supportYourSalon.recommendations.map(sys_item => {
								if (sys_item.sku === item) sales += sys_item.rrp
							});
						});
					} else {
						if (appointment.cartItems.length > 0) {
							appointment.cartItems.map(item => {
								sales += item.price * item.quantity;
							});
						}
					}
				}
			});
		}

		return sales;
	};

	const getYearlyMonthlyStat = (month: number, year: number, staffMember: iStaff, type: string) => {
		let sales = 0;
		
		if (type === CHARTVALUE.NUMBER) {
			props.appointments.map(appointment => {
				const apptDate = moment(new Date(appointment.createdAt));
				if (
					appointment.staffId === staffMember.id &&
					apptDate.month() === month &&
					apptDate.year() === year
				) {
					if (appointment.purchasedItems) {
						sales += appointment.purchasedItems.length;
					} else {
						sales += appointment.cartItems.length;
					}
				}
			});
		} else {
			props.appointments.map(appointment => {
				const apptDate = moment(new Date(appointment.createdAt));
				if (
					appointment.staffId === staffMember.id &&
					apptDate.month() === month &&
					apptDate.year() === year
				) {
				
					if (appointment.purchasedItems) {
						appointment.purchasedItems.map(item => {
							appointment.supportYourSalon.recommendations.map(sys_item => {
								if (sys_item.sku === item) sales += sys_item.rrp
							});
						});
					} else {
						if (appointment.cartItems.length > 0) {
							appointment.cartItems.map(item => {
								sales += item.price * item.quantity;
							});
						}
					}
				}
			});
		}

		return sales;
	};

	const getDailyStat = (
		day: number,
		staffMember: iStaff,
		type: string,
		month?: number | null,
		week?: number | null
	) => {
		let sales = 0;

		/* NUMBER DATA FILTERS */
		if (type === CHARTVALUE.NUMBER) {
			if (month || month === 0) {
				/* FILTER FOR THE MONTH */
				props.appointments.map(appointment => {
					const apptDate = moment(new Date(appointment.createdAt));
					if (
						appointment.staffId === staffMember.id &&
						apptDate.date() === day &&
						apptDate.month() === month
					) {
						if (appointment.purchasedItems) {
							sales += appointment.purchasedItems.length;
						} else {
							sales += appointment.cartItems.length;
						}
					}
				});
			} else {
				/* FILTER FOR THE MONTH */
				props.appointments.map(appointment => {
					const apptDate = moment(new Date(appointment.createdAt));
					if (
						appointment.staffId === staffMember.id &&
						apptDate.date() === day &&
						apptDate.week() === week
					) {
						if (appointment.purchasedItems) {
							appointment.purchasedItems.map(item => {
								appointment.supportYourSalon.recommendations.map(sys_item => {
									if (sys_item.sku === item) sales += sys_item.rrp
								});
							});
						} else {
							appointment.cartItems.map(item => {
								sales += item.price * item.quantity;
							});
						}
					}
				});
			}
			/* MONEY DATA FILTERS */
		} else {
			/* MONTHLY FILTER */
			if (month || month === 0) {
				props.appointments.map(appointment => {
					const apptDate = moment(new Date(appointment.createdAt));
					/* MONTH */
					if (
						appointment.staffId === staffMember.id &&
						apptDate.date() === day &&
						apptDate.month() === month
					) {
						if (appointment.purchasedItems) {
							appointment.purchasedItems.map(item => {
								appointment.supportYourSalon.recommendations.map(sys_item => {
									if (sys_item.sku === item) sales += sys_item.rrp
								});
							});
						} else {
							appointment.cartItems.map(item => {
								sales += item.price * item.quantity;
							});
						}
					}
				});
			} else {
				/* WEEKLY FILTER */
				props.appointments.map(appointment => {
					const apptDate = moment(new Date(appointment.createdAt));
					/* WEEEKLY */
					if (
						appointment.staffId === staffMember.id &&
						apptDate.date() === day &&
						apptDate.week() === week
					) {
						if (appointment.purchasedItems) {
							appointment.purchasedItems.map(item => {
								appointment.supportYourSalon.recommendations.map(sys_item => {
									if (sys_item.sku === item) sales += sys_item.rrp
								});
							});
						} else {
							appointment.cartItems.map(item => {
								sales += item.price * item.quantity;
							});
						}
					}
				});
			}
		}

		return sales;
	};

	const countAllSales = () => {
		let sales = 0;
		props.appointments.map(appointment => {
			if (appointment.purchasedItems) {
				sales += appointment.purchasedItems.length;
			} else {
				sales += appointment.cartItems.length;
			}
		});
		setProductsSales(sales);
	};

	const roundStat = (stat: number) => Math.floor(stat * 100) / 100;

	const XTick = (tick: any) => {
			return (
				<g key={tick.x} transform={`translate(${tick.x},${tick.y + 22})`}>
					<line stroke="rgba(0, 0, 0,0.5)" strokeWidth={1.5} y1={-22} y2={-12} />
					{(tick.tickIndex % 3 === 0 || chartData.length < 8) && (
						<>
						<text
							textAnchor="middle"
							dominantBaseline="middle"
							style={{
								fill: "#000000",
								fontSize: 10
							}}>
							{tick.value}
						</text>
						<text
							textAnchor="middle"
							dominantBaseline="middle"
							y={18}
							style={{
								fill: "#333",
								fontSize: 10
							}}>
							{(duration === "all" || duration === "year") &&
								`${chartType.value === CHARTVALUE.MONEY ? "$" : ""}${calcColTotal(
									tick.value
								)}`}
							</text>
						</>
					)}
				</g>
			);
	};

	const calcColTotal = (value: string) => {
		let total = 0;
		chartData.map(monthData => {
			if (monthData.MONTH === value) {
				Object.keys(monthData).map(key => {
					if (staffNames.includes(key)) total += monthData[key];
				});
			}
		});
		return roundStat(total);
	};

	const calcMonthTotal = () => {
		let sales = 0;
		const now = moment();

		props.appointments.map(appointment => {
			const apptDate = moment(new Date(appointment.createdAt));
			if (apptDate.month() === now.month()) {
				if (chartType.value === CHARTVALUE.NUMBER) {
					if (appointment.purchasedItems) {
						sales += appointment.purchasedItems.length;
					} else {
						sales += appointment.cartItems.length;
					}
				} else {
					if (appointment.purchasedItems) {
						appointment.purchasedItems.map(item => {
							appointment.supportYourSalon.recommendations.map(sys_item => {
								if (sys_item.sku === item) sales += sys_item.rrp
							});
						});
					} else {
						appointment.cartItems.map(item => {
							sales += item.price * item.quantity;
						});
					}
				}
			}
		});

		return `${chartType.value === CHARTVALUE.NUMBER
			? sales
			: sales.toFixed(2)}`;
	};

	const calcAllTotal = () => {
		let sales = 0;

		props.appointments.map(appointment => {
			if (chartType.value === CHARTVALUE.NUMBER) {
				if (appointment.purchasedItems) {
					sales += appointment.purchasedItems.length;
				} else {
					sales += appointment.cartItems.length;
				}
			} else {
				if (appointment.purchasedItems) {
					appointment.purchasedItems.map(item => {
						appointment.supportYourSalon.recommendations.map(sys_item => {
							if (sys_item.sku === item) sales += sys_item.rrp
						});
					});
				} else {
					appointment.cartItems.map(item => {
						sales += item.price * item.quantity;
					});
				}
			}
		});

		return `${chartType.value === CHARTVALUE.NUMBER
			? sales
			: sales.toFixed(2)}`;
	};

	const calcAverageWeek = () => {
		let figure = (productSales / 52).toFixed(1);
		if (chartType.value === CHARTVALUE.MONEY)
			figure = (Number(calcAllTotal()) / 52).toFixed(2);

		return figure
	};

	const calcAverageDay = () => {
		let figure = (productSales / 365).toFixed(1);
		if (chartType.value === CHARTVALUE.MONEY)
			figure = (Number(calcAllTotal()) / 365).toFixed(2);
		return figure;
	};

	const indexBy = duration === "all" || duration === "year" ? "MONTH" : "DAY";
	const fill = [];

	const axisBottom = {
		tickSize: 5,
		tickPadding: 5,
		tickRotation: 0,
		legend:
			duration === "all" ? 
				"MONTH/YEAR" :
			duration === "year" ? 
				"MONTH" : 
			duration === "MONTH" ? 
				"DAY OF THE MONTH": 
				"DAY OF THE WEEK",
		legendPosition: "middle",
		legendOffset: 54,
		renderTick: XTick
	};

	const axisLeft = {
		tickSize: 5,
		tickPadding: 5,
		tickRotation: 0,
		legend:
			chartType.value === CHARTVALUE.NUMBER
				? "Retail Recommended"
				: "Retail Revenue ( $ )",
		legendPosition: "middle",
		legendOffset: -45
	};

	const color = { scheme: "nivo" };
	const fontStyle = {
		fontSize: 8,
		fontWeight: "bold",
		textTransform: "uppercase"
	};

	// recharts graph 
	// https://recharts.org/en-US/examples/SimpleBarChart
	
const data = [
	{
	  name: 'Page A',
	  uv: 4000,
	  pv: 2400,
	  amt: 2400,
	},
	{
	  name: 'Page B',
	  uv: 3000,
	  pv: 1398,
	  amt: 2210,
	},
	{
	  name: 'Page C',
	  uv: 2000,
	  pv: 9800,
	  amt: 2290,
	},
	{
	  name: 'Page D',
	  uv: 2780,
	  pv: 3908,
	  amt: 2000,
	},
	{
	  name: 'Page E',
	  uv: 1890,
	  pv: 4800,
	  amt: 2181,
	},
	{
	  name: 'Page F',
	  uv: 2390,
	  pv: 3800,
	  amt: 2500,
	},
	{
	  name: 'Page G',
	  uv: 3490,
	  pv: 4300,
	  amt: 2100,
	},
  ];
	

	return (
		<InsightsContainer>
			<HeaderRow>
				<div>
					<SectionHeader>
						Retail <span>Recommendations & Value</span>
					</SectionHeader>

				</div>
				<HeaderOption>
				<div>
					<Toggle
						checked={chartType.value === "MONEY"}
						onChange={onTypeChange}
						label={"Products / Value $"}
					/>
					</div>
					<div>
						<DateSelect value={duration} onChange={onDateChange} />
					</div>
				</HeaderOption>
			</HeaderRow>

			{props.isSample && (
							<SampleNotice>We’ve filled out your dashboard with sample data for now. As soon as you have real data we’ll update it here.</SampleNotice>
					)}
					
			<DividerSmall />
			<StatsRow  style={{ opacity: props.isSample ? '0.5' : '1' }}>
				<StatColumn>
					<StatItem>
						<Typography
							align="left"
							style={{ textTransform: "uppercase", fontSize: "0.5rem" }}>
							<FormattedMessage
								id="dashboard.dashretail.all_time"
								defaultMessage={`All Time`}
							/>
						</Typography>
						<Stat>
							{chartType.value === CHARTVALUE.MONEY ? "$" : ""}
							{props.isSample ? 5414 : calcAllTotal()}
						</Stat>
					</StatItem>
					<StatItem>
						<Typography
							align="left"
							style={{ textTransform: "uppercase", fontSize: "0.5rem" }}>
							<FormattedMessage
								id="dashboard.dashretail.month_to_date"
								defaultMessage={`Month-To-Date`}
							/>
						</Typography>
						<Stat>
							{chartType.value === CHARTVALUE.MONEY ? "$" : ""}
							{props.isSample ? 5414 : calcMonthTotal()}
						</Stat>
					</StatItem>
					<StatItem>
						<Typography
							align="left"
							style={{ textTransform: "uppercase", fontSize: "0.5rem" }}>
							<FormattedMessage
								id="dashboard.dashretail.average_per_week"
								defaultMessage={`Average Per Week`}
							/>
						</Typography>
						<Stat>
							{chartType.value === CHARTVALUE.MONEY ? "$" : ""}
							{props.isSample ? 302 : calcAverageWeek()}
						</Stat>
					</StatItem>
					<StatItem>
						<Typography
							align="left"
							style={{ textTransform: "uppercase", fontSize: "0.5rem" }}>
							<FormattedMessage
								id="dashboard.dashretail.average_per_day"
								defaultMessage={`Average Per Day`}
							/>
						</Typography>
						<Stat>
							{chartType.value === CHARTVALUE.MONEY ? "$" : ""}
							{props.isSample ? 200 : calcAverageDay()}
						</Stat>
					</StatItem>
				</StatColumn>
				{props.loading ? (
					<Loader>
						<CircularProgress />
					</Loader>
				) : (
					<ChartContainer>
						<GraphContainer>
								<>
									<BarCompt
										data={props.isSample ? sampleChartData : chartData}
										keys={props.isSample ? sampleStaffNames : staffNames}
										indexBy={indexBy}
										fill={fill}
										axisBottom={axisBottom}
										axisLeft={axisLeft}
										enableLabel={false}
										color={color}
										fontStyle={fontStyle}
										animate={duration !== "all" && duration !== "year"}
										chartType={chartType.value}
										chartDuration={duration}
										customTooltip={({ data }) => {
											let monthString = getMonth(data.MONTH.split("-")[0]);
											let total = 0;
											staffNames.map((n,i) => {
												total += data[n]
											})
											return (
												<div style={{fontSize:14, fontFamily: "Helvetica, Arial", textTransform:"none"}}>
													<span style={{display:"block", fontSize: 16}}>{monthString} {data.MONTH.split("-")[1]}</span>
													{staffNames.map((n,i) => (
														<span key={i} style={{display:"block", fontWeight: 300}}>
															{n} - {chartType.value === CHARTVALUE.MONEY ? "$" : ""}{data[n]}
														</span>
													))}
													<span style={{display:"block"}}>
														Total - {chartType.value === CHARTVALUE.MONEY ? "$" : ""}{total.toFixed(2)}
													</span>
												</div>

											)
										}}
										groupMode={
											"stacked"
											// Object.keys(chartData[0]).length > 8 ? "stacked" : "grouped"
										}
									/>
								</>
						</GraphContainer>
					</ChartContainer>
				)}
			</StatsRow>
		</InsightsContainer>
	);
}
